import { addToast } from '../components/Toast/Toast'
import { ACCEPTED_MEDIA_FORMATS } from '../constants/constants'
import { type IFile } from '../types/interfaces/IFile'
import { type IPost, type IPostMediaPreview } from '../types/interfaces/ITypes'

export const getSupportedMediaFormats = () => {
    return ACCEPTED_MEDIA_FORMATS.join()
}

export const validateMedia = async (mediaFile: File, t: any) => {
    return await new Promise((resolve, reject) => {
        if (!ACCEPTED_MEDIA_FORMATS.includes(mediaFile.type)) {
            addToast('error', `${t('error:mediaFormatIsNotSupported')}!`)
            resolve(false)
        }
        if (mediaFile.type.includes('image/')) {
            const url = URL.createObjectURL(mediaFile)
            const img = new Image()
            img.src = url
            img.onload = () => {
                if (img.width >= 10000 || img.height >= 10000) {
                    addToast('error', `${t('error:imageSizeIsWrong')}!`)
                    resolve(false)
                }

                resolve(true)
            }
        } else if (
            mediaFile.type.includes('video/') ||
            mediaFile.type.includes('audio/')
        ) {
            const videoUploadLimit = 13221225472
            // const videoUploadLimit = 1073741824;
            if (mediaFile.size > videoUploadLimit) {
                addToast('error', `${t('error:mediaSizeIsWrong')}!`)
                resolve(false)
            }
            resolve(true)
        }
    })
}

const asyncFilter = async (arr: any[], predicate: any) =>
    await Promise.all(arr.map(predicate)).then(results =>
        arr.filter((_v: any, index: number) => results[index])
    )

export const validateMediaFiles = async (mediaFiles: File[], t: any) => {
    return await asyncFilter(
        mediaFiles,
        async (file: File) => await validateMedia(file, t)
    )
}

export const extractMedia = (post: IPost) => {
    let tmpArr: IFile[] = []

    if (post.photos) {
        tmpArr = [...post.photos]
    }
    if (post.videos) {
        tmpArr = [...tmpArr, ...post.videos]
    }
    if (post.sounds) {
        tmpArr = [...tmpArr, ...post.sounds]
    }

    const sortedResult = tmpArr.sort((a, b) => a.order - b.order)

    return sortedResult
}

export const orderMediaFiles = (
    data: IPost
): { media: IFile[]; previews: IPostMediaPreview[] } => {
  const photos         = Array.isArray(data?.photos) ? data.photos : [];
  const videos         = Array.isArray(data?.videos) ? data.videos : [];
  const sounds         = Array.isArray(data?.sounds) ? data.sounds : [];
  const photosPreview  = Array.isArray(data?.photos_preview) ? data.photos_preview : [];
  const videosPreview  = Array.isArray(data?.videos_preview) ? data.videos_preview : [];

  const sortByOrder = <T extends { order?: number }>(a: T, b: T) =>
    (a?.order ?? 0) - (b?.order ?? 0);

  const media = [...photos, ...videos, ...sounds].sort(sortByOrder);
  const previewsAll = [...photosPreview, ...videosPreview].sort(sortByOrder);

  // unified unlock check works for posts *and* messages
  const hasRealMedia = media.length > 0;
  const isPurchased =
    Boolean((data as any)?.purchased_date) ||
    Boolean((data as any)?.isPurchased) ||
    Boolean((data as any)?.is_unlocked);

  const isUnlocked = isPurchased || hasRealMedia;

  if (isUnlocked) {
    // prefer real media; if missing for some edge case, fall back to previews
    return { media, previews: hasRealMedia ? [] : previewsAll };
  }
  // locked: return previews, no media
  return { media: [], previews: previewsAll };
};

type WithUnlockFlags = {
  purchased_date?: string | null;
  is_unlocked?: boolean;
  isPurchased?: boolean;
};

export const computeUnlockStatus = (data: Partial<IPost> & WithUnlockFlags): boolean => {
  // Safely count media without assertions
  const hasRealMedia =
    (data.photos?.length ?? 0) +
    (data.videos?.length ?? 0) +
    (data.sounds?.length ?? 0) > 0;

  // Treat any of these as unlocked
  const hasUnlockFlag = !!(data.purchased_date ?? data.is_unlocked ?? data.isPurchased);

  return hasUnlockFlag || hasRealMedia;
};


export type Orientation = 'Portrait' | 'Landscape'
